import { ReasonType } from "@rooster/types";
import { useEffect, useState } from "react";
import { baseUrlServer } from "../services/api/common";
import { axiosWithRetry } from "../services/api/utils/axiosRetry";

type CandidateReasonsResponse = {
  reasons: ReasonType[];
  settings: {
    cancel?: {
      show?: boolean;
      candidate?: { show?: boolean; require?: boolean; showComment?: boolean };
    };
    reschedule?: {
      show?: boolean;
      candidate?: { show?: boolean; require?: boolean; showComment?: boolean };
    };
  };
};

export const useCandidateReasons = ({
  availabilityId,
  operation,
  enabled = true
}: {
  availabilityId: string | null;
  operation: "cancel" | "reschedule";
  enabled?: boolean;
}) => {
  const [reasons, setReasons] = useState<ReasonType[]>([]);
  const [isRequired, setIsRequired] = useState(false);
  // Defaults ON: tenants that never touched the setting keep the comment box.
  const [showComment, setShowComment] = useState(true);
  // Start loading whenever a fetch is going to happen: `reasons`/`isRequired`
  // read as "no reasons, not required" until the response lands, so a caller
  // that only checks those would let a required reason be skipped in the gap
  // before the effect runs.
  const [loading, setLoading] = useState(Boolean(availabilityId) && enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // No fetch to wait on — clear any load left running by a previous id.
    if (!availabilityId || !enabled) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchReasons = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosWithRetry(
          `${baseUrlServer}/candidate/reasons/${encodeURIComponent(
            availabilityId
          )}?operation=${operation}`,
          "get",
          { timeout: 10000 }
        );

        if (cancelled) return;
        const data = response.data as CandidateReasonsResponse;
        const operationSettings = data?.settings?.[operation];
        const isEnabled = operationSettings?.candidate?.show ?? false;
        setReasons(isEnabled ? (data?.reasons ?? []) : []);
        setIsRequired(
          isEnabled ? (operationSettings?.candidate?.require ?? false) : false
        );
        setShowComment(operationSettings?.candidate?.showComment ?? true);
      } catch {
        if (cancelled) return;
        setReasons([]);
        setIsRequired(false);
        setShowComment(true);
        setError("Failed to load reasons");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReasons();
    return () => {
      cancelled = true;
    };
  }, [availabilityId, operation, enabled]);

  return { reasons, isRequired, showComment, loading, error };
};
